// Copyright (c) Ping. All rights reserved.

// We avoid the `@ping/ui` barrel because it fan-outs into
// `imageeditor/exifOrientation.ts`, which dynamically imports
// `exifr` — a workspace dep that pre-bundling chokes on in some
// environments. Pinning the submodules keeps this entry-point lean and
// improves chunk granularity for code-split.
import { MessagingClient } from 'ping-openmls-sdk';

import type {
  AuthenticatedUser as AuthenticatedUser,
  AuthFailureReason as AuthFailureReason,
} from '@ping/ui';
import { resetCallGroups } from '@ping/ui/src/calls/callGroupRegistry';
import { bootstrapSession, useSessionStore } from '@ping/ui/src/messaging/sessionStore';
import type { IdentityHandle } from '@ping/ui/src/platform/identity';
import {
  IdentityCorruptionError,
  IdentityKeyUnavailableError,
  identityManager,
} from '@ping/ui/src/platform/identity.web';
// The platform-extension resolver (Vite for web) would collapse the
// abstract `PingStorage` to the concrete `PingStorageWeb`, but `tsc`
// has no platform-extension awareness and still sees the abstract
// class. Pin the concrete `.web` impl explicitly here: this file is
// web-only — `apps/desktop/` has its own bootstrapper that uses the
// macOS variant.
import { PingStorageWeb } from '@ping/ui/src/storage-mls/PingStorage.web';
import {
  registerCurrentUserAccountId,
  registerCurrentUserDisplayName,
  registerCurrentUserMlsUserId,
  registerSelfDeviceIdsHex,
} from '@ping/ui/src/sync/registerCurrentUser';
import type { CursorStore } from '@ping/ui/src/transport-mls/cursorStore';
import { PingTransport } from '@ping/ui/src/transport-mls/PingTransport';

import { log } from '@core/log/log';
import { del, get, post, put } from '@core/network/api';
import { getDeviceSigningSecretBytes } from '@core/network/deviceKeys';
import {
  ensureFreshAccessToken,
  getAccessToken,
  handleTerminalAuthFailure,
} from '@core/network/interceptor';
import { createSocketAdapter } from '@core/network/socketAdapter';
import { listSelfDeviceIdsHex } from '@core/network/users';
import { retrieveIdentity, storeIdentity } from '@core/persistence/identity';
import { registerWebPush } from '@core/push/webPushRegistration';
import {
  deleteSyncCursor,
  getSyncCursor,
  putSyncCursor,
  snapshotSyncCursors,
} from '@core/storage/idb';

import { mountConsoleApi } from './consoleApi';
import { startEphemeralSession } from './ephemeralSession';
import { clearReloadGuard, safeReload } from './reloadGuard';
import { maybeTopUpKeyPackages, replenishKeyPackages } from './replenishKeyPackages';

/**
 * Wrapper of `bootstrapSession` from `@ping/ui` that closes the
 * GAP-3 / GAP-4 / GAP-5 break — after SMS verification the web app
 * never drove the Zustand session store, leaving `useSession()` null
 * and the `ChatRoom` send path broken.
 *
 * Lifecycle contract: this function is the **only** path that
 * transitions `useSessionStore` to `authenticated` on web. It is
 * idempotent (a second call while the store is already authenticated
 * resolves immediately) and dedupes against an in-flight bootstrap
 * via `useSessionStore.bootstrapPromise`.
 */
export interface BootstrapWebSessionInput {
  readonly tokens: AuthenticatedUser['tokens'];
  readonly identity: AuthenticatedUser['identity'];
}

// IndexedDB-backed `CursorStore` shared by every `PingTransport` instance.
// One per tab is fine — the underlying IDB connection inside `idb.ts` is
// also a singleton. We construct lazily so importing this module from
// non-browser contexts (e.g. SSR pre-render) doesn't try to open IDB.
const webCursorStore: CursorStore = {
  get: (conversationIdHex) => getSyncCursor(conversationIdHex),
  put: (conversationIdHex, cursor) => putSyncCursor(conversationIdHex, cursor),
  delete: (conversationIdHex) => deleteSyncCursor(conversationIdHex),
  snapshot: () => snapshotSyncCursors().then((rows) => rows),
};

/**
 * Called by `PingTransport` the first time the BE 404s a
 * fetchSince / send for a given conv. Pure cleanup:
 *   * Deletes the Dexie `conversations` row + its `messages` so the
 *     chat list updates (Dexie's `liveQuery` propagates immediately).
 *   * Drops any draft + the persisted sync cursor for the conv.
 *   * Fires a single toast per session so the user knows what
 *     happened.
 *
 * Best-effort: every step is independently try-caught. If pruning
 * fails the conv just stays in the list — no worse than the
 * pre-fix behaviour, and the next reload will retry.
 */
function pruneOrphanedConversation(conversationIdHex: string): void {
  void (async () => {
    // Delegate to the SHARED server-orphan write (`markConversationServerOrphaned`)
    // so the transport's consecutive-404 verdict and the members-endpoint
    // `not_member` verdict take EXACTLY the same soft-hide path (single source of
    // truth: `orphanedAt`, cursor drop, recoverable, no data loss). The transport
    // only fires this after N CONSECUTIVE 404s, so a transient blip never reaches
    // here. No user-facing toast — orphaning is a routine, silent reconcile.
    const { markConversationServerOrphaned } = await import('@core/persistence/conversations');
    await markConversationServerOrphaned(conversationIdHex, 'transport_fetch_404');
  })();
}

// Module-level handle on the active PingTransport. The MLS chat path
// needs to prime side-channel info on the transport (specifically the
// recipient device for Welcome envelopes that addMembers() emits) but
// the Zustand session store only carries the MessagingClient. Keeping
// the reference here avoids reshuffling the public session API.
// Stash the live transport on `globalThis`, NOT a module-local `let`. A plain
// `let` resets to `null` whenever this module is re-evaluated — most commonly a
// Vite HMR hot-update in dev, but also any duplicate module instance. The SDK's
// Web Worker survives that re-eval and keeps using its (already-wired) transport
// to POST, so a null module-local left `getCurrentTransport()` returning null
// while a working session ran — which silently skipped welcome-recipient priming
// (`transport.setWelcomeRecipients`) and made group add-member send a Welcome
// with no `?recipient=` → BE 400. A `globalThis` slot persists across re-eval,
// so the host always reaches the same transport instance the worker is using.
const TRANSPORT_GLOBAL_KEY = '__pingCurrentTransport__';

function setCurrentTransport(t: PingTransport | null): void {
  (globalThis as Record<string, unknown>)[TRANSPORT_GLOBAL_KEY] = t;
}

export function getCurrentTransport(): PingTransport | null {
  return (
    ((globalThis as Record<string, unknown>)[TRANSPORT_GLOBAL_KEY] as
      | PingTransport
      | null
      | undefined) ?? null
  );
}

/**
 * Reset all module-scoped session state on teardown / account switch.
 *
 * The Zustand session store + storage wipe (clearAllLocalSessionState) handle
 * the SDK client and persisted data, but these two module singletons live
 * outside that and were previously never cleared — so on a logout→login to a
 * DIFFERENT account, an in-flight WS frame on the old transport, or the chat
 * adapter's `getCurrentUserAccountId()` read, could briefly attribute the old
 * account's data to the new session. Dispose the transport (force-closes its
 * socket and latches `disposed` so late frames are dropped) and null both
 * registries. Idempotent; always safe to call.
 */
export function resetWebSessionRegistries(): void {
  const existing = getCurrentTransport();
  if (existing) {
    try {
      existing.dispose();
    } catch {
      /* best-effort */
    }
  }
  setCurrentTransport(null);
  registerCurrentUserAccountId(null);
  registerCurrentUserMlsUserId(null);
  // Tear down the durable call-group registry: clear the in-memory ids and
  // UNWIRE the persist hook so no call-group write (purge/persist) can fire
  // during or after the local wipe — that would resurrect purged rows in Dexie
  // or hold an IDB handle open against the teardown — and so a signed-out
  // browser carries no call-group state into the next account. Re-armed by
  // `initCallGroupPersistence` on the next sign-in.
  resetCallGroups();
}

// Resolve the WebSocket URL the transport should connect to.
// • If `VITE_API_BASE_URL` is set (production / non-default dev), derive
//   the WS scheme from that absolute URL.
// • Otherwise, return a same-origin URL so the Vite dev proxy forwards
//   the upgrade to `DEFAULT_API_PROXY_TARGET` without tripping the
//   browser's mixed-content / CORS preflight.
//
// The route is `/v1/stream` — the ws-fanout service (`services/ws-fanout`)
// only registers that path; the older `/v1/ws` endpoint was retired with
// the move off the Go API server. Auth rides as `?token=<jwt>` query
// param: `ws_handler` extracts `Query<WsQuery>` and verifies by calling
// `GET /v1/me/session` on the API.
function deriveWsUrlFromApiBase(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  if (raw.length > 0) {
    try {
      const u = new URL(raw);
      const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${u.host}/v1/stream`;
    } catch {
      // fall through to same-origin
    }
  }
  if (typeof window !== 'undefined' && window.location) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/v1/stream`;
  }
  return 'wss://openmls.ping.live/v1/stream';
}

export class BootstrapFailure extends Error {
  readonly reason: AuthFailureReason;

  constructor(reason: AuthFailureReason, message?: string) {
    super(message ?? `bootstrap.failed:${reason}`);
    this.name = 'BootstrapFailure';
    this.reason = reason;
  }
}

// Tracks how many times the current tab has hit an SDK "storage
// corruption" bootstrap error. The first occurrence is treated as a
// possibly-transient IndexedDB blip and recovered with a plain reload
// (no state destroyed); only a corruption that survives that reload is
// treated as genuine and triggers the destructive device wipe. Stored in
// sessionStorage so it survives the reload but resets on tab close, and
// cleared on any successful bootstrap.
const STORAGE_CORRUPTION_RETRY_KEY = 'ping:bootstrap:storageCorruptionRetries';
const STORAGE_CORRUPTION_MAX_RETRIES = 1;

// Low-watermark KeyPackage top-up state. `latestKpClient` tracks the current
// session's SDK client so the resume listeners (registered once) always top up
// against the live client rather than a stale one from a prior bootstrap.
let latestKpClient: { freshKeyPackage(): Promise<Uint8Array> } | null = null;
let topUpListenersRegistered = false;

// Survives the reload that follows an SDK-storage wipe (realign / corruption).
// Signals the next bootstrap to FORCE a full KeyPackage replenish because the
// wipe destroyed the private init keys behind any KeyPackages still published
// server-side — the server count would otherwise look healthy but hand peers
// undecryptable KeyPackages.
const FORCE_REPLENISH_MARKER = 'ping:kp:forceReplenishAfterWipe';

function markForceReplenishAfterWipe(): void {
  try {
    sessionStorage.setItem(FORCE_REPLENISH_MARKER, '1');
  } catch {
    /* best-effort */
  }
}

function consumeForceReplenishMarker(): boolean {
  try {
    const set = sessionStorage.getItem(FORCE_REPLENISH_MARKER) === '1';
    if (set) sessionStorage.removeItem(FORCE_REPLENISH_MARKER);
    return set;
  } catch {
    return false;
  }
}

// SDK↔auth device-id mismatch recovery. The ONLY cause that survives a plain
// reload is stale SDK MLS storage: the SDK loaded a device id persisted by a
// PRIOR init/identity instead of adopting the device signing key we pass at init
// (whose `SHA-256(pubkey)` IS the auth device id). A non-destructive reload just
// re-loads the same stale device — which is exactly why the previous multi-retry
// path "reload-looped" without ever realigning. So on the first mismatch we wipe
// ONLY the SDK MLS storage (preserving the device signing key, identity seed AND
// credentials) and reload once: the next init then has no persisted device and
// adopts our signing key → the ids match by construction, silently (no re-OTP).
// One-shot guard: if the mismatch SURVIVES that realign, the device signing key
// itself is wrong (not just stale storage) and can't be fixed client-side, so we
// stop auto-reloading (which would loop) and surface the welcome screen with
// credentials intact for a manual re-verify.
const DEVICE_MISMATCH_RECOVERED_KEY = 'ping:bootstrap:deviceMismatchRecovered';

// Durable "this device has completed an authenticated bootstrap at least once"
// marker, stored in Dexie `settings_kv` (same lifecycle as the device signing
// key: survives reload / transient logout / realign / fresh-identity mint;
// cleared only by a full `forgetDevice` sign-out). Used to distinguish a genuine
// fresh signup from an established device that has lost its identity seed WITHOUT
// a sign-out (a storage-eviction hazard) — see `loadOrCreateIdentity`.
const DEVICE_ESTABLISHED_KV_KEY = 'device.established.v1';

async function markDeviceEstablished(): Promise<void> {
  try {
    const { db } = await import('@core/persistence/db');
    const existing = await db.table('settings_kv').get(DEVICE_ESTABLISHED_KV_KEY);
    if (existing !== undefined) return; // write-once; don't churn.
    await db
      .table('settings_kv')
      .put({ key: DEVICE_ESTABLISHED_KV_KEY, value: { at: Date.now() }, updatedAt: Date.now() });
  } catch (err) {
    log.warn({ event: 'session.markEstablished.failed', message: (err as Error).message });
  }
}

async function isDeviceEstablished(): Promise<boolean> {
  try {
    const { db } = await import('@core/persistence/db');
    return (await db.table('settings_kv').get(DEVICE_ESTABLISHED_KV_KEY)) !== undefined;
  } catch {
    return false;
  }
}

// In-flight bootstrap dedupe. Two concurrent callers can hit
// `bootstrapWebSession` on the same tick:
//
//   1. The auth flow's `onAuthenticated` handler fires when the user
//      completes sign-up.
//   2. App.tsx's silent-reauth `useEffect` re-runs the instant the
//      session store status changes (which happens *because* of (1)),
//      finds the freshly-stored refresh token + identity, and calls
//      `bootstrapWebSession` again.
//
// The synchronous `currentStatus.kind === 'authenticated'` check
// can't catch this race — neither call has finished by the time the
// other reaches the check. The previous behaviour was that both ran
// fully end-to-end, double-firing the SDK `init`, doubling
// `console.api.mounted`, doubling `keyPackages.replenish.ok`, etc.
//
// Cache the first call's Promise; concurrent callers `await` it.
// Cleared on resolution / rejection so a subsequent (sequential)
// bootstrap can run normally — for example after sign-out.
let inFlight: Promise<void> | null = null;

export async function bootstrapWebSession(input: BootstrapWebSessionInput): Promise<void> {
  if (inFlight) {
    log.info(
      { event: 'session.bootstrap.dedupe' },
      'concurrent bootstrap detected; awaiting in-flight call',
    );
    return inFlight;
  }
  inFlight = (async () => {
    try {
      await runBootstrap(input);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Realign the SDK's device id with the auth layer by wiping ONLY the SDK's
 * persisted MLS storage (`ping-mls-storage`) plus the conversation-keyed Dexie
 * caches + WS-resume cursors, while PRESERVING the device signing key
 * (`__device_signing_key` lives in Dexie `settings_kv`, untouched here), the
 * identity seed (`ping-identity`) and the credentials. The next
 * `MessagingClient.init` then has no stale persisted device to load, so it
 * adopts the device signing key we pass at init — whose `SHA-256(pubkey)` IS the
 * auth `device_id` — and the two ids match by construction. This is the only
 * client-side realign available while the SDK lacks an `ownDeviceId` override
 * (see `sdkDeviceId.ts`). Mirrors the fresh-identity wipe in
 * `loadOrCreateIdentity`. Best-effort: a wipe failure must not gate the reload.
 */
async function wipeSdkStorageForRealign(storage: PingStorageWeb): Promise<void> {
  // The wipe destroys the private init keys behind any KeyPackages this device
  // has published, so the post-reload bootstrap MUST re-upload a fresh batch
  // even if the server count still looks healthy — otherwise peers claim
  // undecryptable KeyPackages and their Welcomes fail.
  markForceReplenishAfterWipe();
  try {
    await storage.forgetSession();
  } catch (err) {
    log.warn({
      event: 'session.bootstrap.realign.forgetSessionFailed',
      message: (err as Error).message,
    });
  }
  try {
    const { clearAllData } = await import('@core/storage/idb');
    await clearAllData();
  } catch (err) {
    log.warn({
      event: 'session.bootstrap.realign.cursorClearFailed',
      message: (err as Error).message,
    });
  }
  // Drop the conversation-keyed Dexie caches so a PRIOR identity's chats don't
  // linger as ghosts against the realigned (member-of-nothing-yet) device.
  // `settings_kv` is left UNTOUCHED so the device signing key survives.
  try {
    const { db } = await import('@core/persistence/db');
    await Promise.all([
      db.table('messages').clear(),
      db.table('conversations').clear(),
      db.table('drafts').clear(),
      db.table('attachments_meta').clear(),
    ]);
  } catch (err) {
    log.warn({
      event: 'session.bootstrap.realign.dexieClearFailed',
      message: (err as Error).message,
    });
  }
}

async function runBootstrap(input: BootstrapWebSessionInput): Promise<void> {
  // The `tokens` parameter is intentionally a marker — the actual
  // access token lives in module-scope inside core/network/interceptor.
  // We confirm the marker matches reality before consenting to bootstrap.
  if (!input.tokens.hasAccessToken || getAccessToken() === null) {
    log.warn({ event: 'session.bootstrap.rejected' }, 'no access token in memory');
    throw new BootstrapFailure('invalidCredential');
  }

  const currentStatus = useSessionStore.getState().status;
  if (currentStatus.kind === 'authenticated') {
    log.info({ event: 'session.bootstrap.skip' }, 'already authenticated');
    return;
  }

  log.info({ event: 'session.bootstrap.begin' });

  const storage = new PingStorageWeb();

  let identityHandle: IdentityHandle;
  let isFreshIdentity = false;
  try {
    const result = await loadOrCreateIdentity(storage);
    identityHandle = result.handle;
    isFreshIdentity = result.isFreshIdentity;
  } catch (error) {
    log.error(
      {
        event: 'session.bootstrap.identityFailed',
        kind: errorKind(error),
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      },
      'identity load failed',
    );
    throw new BootstrapFailure('serverError');
  }

  const transport: PingTransport = new PingTransport({
    api: {
      // The MLS transport pins `Content-Type: application/cbor` (and
      // `Accept: application/cbor` on /v1/sync) via the third `opts`
      // arg. Forward it through; dropping it here is what produced
      // the 415 "Content-Type must be application/cbor" failure.
      // Shorthand method syntax (`get<T>(...) { ... }`) avoids the
      // arrow-generic JSX-disambiguation hazard: Vite's esbuild
      // treats `.ts` as `.tsx` so a bare `<T>(...)` arrow parses as
      // a JSX element open. With shorthand methods the generic is
      // unambiguous because the method name precedes the `<`.
      get<T>(path: string, opts?: unknown) {
        return get<T>(path, (opts ?? {}) as Parameters<typeof get>[1]);
      },
      post<T>(path: string, body?: unknown, opts?: unknown) {
        return post<T>(path, body, (opts ?? {}) as Parameters<typeof post>[2]);
      },
      put<T>(path: string, body?: unknown, opts?: unknown) {
        return put<T>(path, body, (opts ?? {}) as Parameters<typeof put>[2]);
      },
      delete<T>(path: string, opts?: unknown) {
        return del<T>(path, (opts ?? {}) as Parameters<typeof del>[1]);
      },
    },
    // Wire `onReconnect` to a CONDITIONAL refresh: mint a fresh access token
    // ONLY when the current one is actually near expiry (`ensureFreshAccessToken`
    // no-ops otherwise). A WS reconnect with a still-valid access token must NOT
    // POST the single-use refresh token — that stale-token round-trip 401s and
    // used to tear the session down (the "random logout despite a working
    // session" bug). When the token IS near expiry this refreshes once
    // (idempotent via `inFlightRefresh`); transient failures are swallowed, and
    // a genuine invalid-token only tears down once the access token is itself
    // unusable (see `handleTerminalAuthFailure`). Errors here are swallowed so
    // the reconnect loop falls through to the next attempt.
    socket: createSocketAdapter({
      onReconnect: () => ensureFreshAccessToken().catch(() => undefined),
    }),
    log: {
      debug: (msg, meta) => log.debug({ ...(meta ?? {}), msg }, 'transport'),
      info: (msg, meta) => log.info({ ...(meta ?? {}), msg }, 'transport'),
      warn: (msg, meta) => log.warn({ ...(meta ?? {}), msg }, 'transport'),
      error: (msg, err, meta) =>
        log.error({ ...(meta ?? {}), msg, kind: errorKind(err) }, 'transport'),
    },
    // WebSocket lives on the BE — same host as the REST API, not the
    // Vite dev origin. Without this override the transport would fall
    // back to a `<window.location.host>` URL which on a localhost dev
    // server is `ws://localhost:3000/v1/stream` (no server), so
    // realtime push never works.
    wsUrl: deriveWsUrlFromApiBase(),
    // Persist per-conversation WS-resume cursors in IndexedDB so a tab
    // reload / dropped connection re-joins exactly where the previous
    // session left off — no full REST catch-up, no duplicate envelopes.
    cursorStore: webCursorStore,
    // Auto-prune Dexie conv rows whose underlying MLS state is gone.
    // Fires when the BE 404s a fetchSince or POST /v1/messages — the
    // canonical signal that the local chat list points at an MLS
    // group the BE doesn't know about. Without this, the user sees a
    // broken "ghost" row in the chat list with no way to recover
    // except wiping all local state.
    //
    // A toast fires at most once per page session so the user
    // understands that the row disappeared on purpose — repeated
    // orphans during the same load are batched into the same toast.
    onConversationOrphaned: pruneOrphanedConversation,
  });
  // Stash the constructed transport so chat-feature code that needs to
  // prime out-of-band hints (e.g. the Welcome `?recipient=` query
  // param) can reach it without threading it through React context.
  // Cleared on session teardown.
  setCurrentTransport(transport);

  // Re-hydrate the DURABLE call-group registry + wire the persist/purge hook
  // BEFORE the transport starts draining conversations. A per-call MLS group's
  // `call:<id>` name is local to the caller, so a callee can only recognise a
  // call group by this registry — and without durability every reload would
  // re-leak call groups as ghost "New chat" rows (and fire a spurious "someone
  // started a conversation"). This makes that suppression survive reloads and
  // retroactively purges any row that already leaked.
  try {
    const { initCallGroupPersistence } = await import('@calls/callGroupPersistence');
    await initCallGroupPersistence();
  } catch (err) {
    log.warn({ event: 'callGroup.persistence.initFailed', message: (err as Error).message });
  }

  // Instant remote sign-out. When the user signs out a device — or "all
  // devices" — from Settings, the backend broadcasts a
  // `{ type: "session_revoked", all?: boolean, device_ids?: string[] }`
  // account event to every live WS connection on the account (same fanout
  // channel as `device_added`). A device that finds itself in the revoked
  // set tears down THROUGH THE SAME TERMINAL PATH as an
  // `X-Ping-Reauth-Required` 403 — `handleTerminalAuthFailure(force)` →
  // the app's `goUnauthenticated({ fullWipe: true })` → full local wipe +
  // welcome — so even a dormant linked device signs out the instant the
  // push lands, instead of waiting for its next gated request/refresh.
  transport.onAccountEvent((event) => {
    if (event['type'] !== 'session_revoked') return;
    const all = event['all'] === true;
    const ids = Array.isArray(event['device_ids']) ? (event['device_ids'] as unknown[]) : [];
    const mine = input.identity.deviceId;
    const revoked = all || ids.some((d) => typeof d === 'string' && d === mine);
    if (!revoked) return;
    log.info({ event: 'session.accountEvent.sessionRevoked', all });
    void handleTerminalAuthFailure('accountEvent.sessionRevoked', { force: true });
  });

  // Pull the device signing secret so the MLS SDK adopts the SAME
  // signing key the auth layer is using. The SDK derives its
  // `device_id` from `SHA-256(signing_pubkey)`; the auth layer
  // computes the JWT `device_id` claim the same way. With the SDK
  // adopting our key, both layers' device ids match by construction,
  // and the message-service's `expected_sender_device` validation
  // passes on every envelope without server-side relax.
  let deviceSigningSecretKey: Uint8Array | undefined;
  try {
    deviceSigningSecretKey = await getDeviceSigningSecretBytes();
  } catch (err) {
    // Non-extractable key (shouldn't happen — we created it with
    // `extractable: true`) or a storage hiccup. Fall back to the
    // SDK's random-key path; the BE will still 400 on send until
    // the alignment is restored, but bootstrap completes.
    log.warn({
      event: 'session.bootstrap.signingSecretUnavailable',
      message: (err as Error).message,
    });
  }

  try {
    await bootstrapSession({
      identity: identityHandle,
      deviceLabel: 'web',
      storage,
      transport,
      messagingClient: MessagingClient,
      ...(deviceSigningSecretKey ? { deviceSigningSecretKey } : {}),
    });
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    log.error(
      {
        event: 'session.bootstrap.failed',
        kind: errorKind(error),
        message: msg,
        stack: error instanceof Error ? error.stack : undefined,
      },
      'bootstrapSession threw',
    );

    // The SDK's `StorageCorruption: Failed to decrypt stored value`
    // means the WASM storage layer was written under a different key
    // binding than the one we're presenting now. Most commonly this
    // hits after the identity-wrap-key fix landed (old ciphertext is
    // unreadable) or after a partial pre-pair init left stale rows
    // under a different identity. The only safe recovery is:
    //
    //   1. Wipe every layer of local state.
    //   2. Hard-reload the tab so no in-flight code path (interceptor
    //      auto-refresh, React effect re-fire, observer callback) can
    //      sneak a stale access token / signing key back into memory
    //      and start a *new* bootstrap with mismatched material —
    //      that path produced the `deviceIdDiag … match: false`
    //      footgun, where the SDK device id (from a freshly-generated
    //      signing key) disagreed with the auth-layer device id (from
    //      a JWT minted off the prior refresh token).
    //
    // The reload effectively turns this catch into an unauthenticated
    // cold-start — the next page load sees zero tokens, falls through
    // to the welcome screen.
    if (msg.includes('StorageCorruption') || msg.includes('Failed to decrypt stored value')) {
      // A *transient* IndexedDB failure (quota pressure, Safari evicting IDB
      // under storage pressure, a multi-tab open lock, a partial WASM/provider
      // open) can surface with the SAME message as genuine ciphertext
      // corruption. Wiping the device on the FIRST hit turns a recoverable
      // blip into a permanent logout. So: the first occurrence reloads WITHOUT
      // touching any local state (the SDK simply re-opens its storage); only a
      // corruption that PERSISTS across that reload is treated as real and
      // triggers the destructive device wipe.
      let priorRetries = 0;
      try {
        priorRetries =
          Number(sessionStorage.getItem(STORAGE_CORRUPTION_RETRY_KEY) ?? '0') || 0;
      } catch {
        // sessionStorage unavailable — can't track the retry, so fall straight
        // through to the (safe) destructive path below.
        priorRetries = STORAGE_CORRUPTION_MAX_RETRIES;
      }

      if (priorRetries < STORAGE_CORRUPTION_MAX_RETRIES) {
        log.warn({
          event: 'session.bootstrap.sdkStorageCorrupted_retrying',
          attempt: priorRetries + 1,
        });
        try {
          sessionStorage.setItem(
            STORAGE_CORRUPTION_RETRY_KEY,
            String(priorRetries + 1),
          );
        } catch {
          /* best-effort */
        }
        if (typeof window !== 'undefined' && safeReload('bootstrap.storageCorrupted.retry')) {
          await new Promise(() => {});
        }
        throw new BootstrapFailure(
          'invalidCredential',
          'local storage was temporarily unreadable — retrying',
        );
      }

      // Persisted across a reload → the SDK's MLS store is genuinely
      // unreadable. Wipe ONLY the SDK/MLS storage + conversation caches and
      // reload — NEVER the refresh token, identity seed, or device signing key.
      // A corrupt MLS store is not proof the refresh token (a separate,
      // independently-valid credential in a different store) is dead; destroying
      // it (the old `forgetDevice: true` / credential wipe) forced a full re-OTP
      // and, because the device is still server-side active + identity-pinned,
      // risked a 403 `identity_mismatch` lockout. Preserving credentials means
      // the reload re-inits a clean MLS store and silently re-auths — the user
      // keeps their session and conversations resync from the BE.
      log.warn({ event: 'session.bootstrap.sdkStorageCorrupted_recovering' });
      try {
        sessionStorage.removeItem(STORAGE_CORRUPTION_RETRY_KEY);
      } catch {
        /* best-effort */
      }
      // Credential-preserving: forgetSession() + IDB cursor + Dexie chat caches;
      // `settings_kv` (device signing key) and the auth tokens are left intact.
      await wipeSdkStorageForRealign(storage);
      if (typeof window !== 'undefined' && safeReload('bootstrap.storageCorrupted.recover')) {
        // Stall the rest of this function — the reload will abort
        // everything else; we just want to make sure nothing else
        // throws or sets state in the milliseconds before navigation.
        await new Promise(() => {});
      }
      // Preserve-credentials failure: the caller routes this through
      // `goUnauthenticated` WITHOUT fullWipe, so credentials survive and the
      // next load silently re-authenticates.
      throw new BootstrapFailure(
        'invalidCredential',
        'local MLS storage was unreadable — it was reset; your session is preserved',
      );
    }

    throw new BootstrapFailure(mapBootstrapError(error));
  }

  const finalStatus = useSessionStore.getState().status;
  if (finalStatus.kind !== 'authenticated') {
    // Defensive: bootstrapSession resolved but the store is not in the
    // authenticated state (interleaved tear-down, race with sign-out).
    log.error(
      { event: 'session.bootstrap.statusMismatch', kind: finalStatus.kind },
      'unexpected post-bootstrap status',
    );
    throw new BootstrapFailure('serverError');
  }

  log.info({ event: 'session.bootstrap.ok' });
  // Bootstrap succeeded — reset the reload-loop breadcrumb so a legitimate
  // one-off recovery reload never accumulates toward the loop threshold.
  clearReloadGuard();
  // Record a durable "this device completed an authenticated bootstrap" marker.
  // It lives in `settings_kv`, so it survives reloads / transient logouts /
  // realign / a fresh-identity mint (all preserve `settings_kv`) but is wiped by
  // a full sign-out (`forgetDevice` clears `settings_kv`). `loadOrCreateIdentity`
  // reads it to tell a genuine fresh signup (marker ABSENT) from an ESTABLISHED
  // device whose identity seed went missing without a sign-out (marker PRESENT →
  // suspect eviction), so the latter never wipes the user's conversations. */
  void markDeviceEstablished();

  // Bootstrap reached `authenticated` — clear the transient-recovery guards so
  // a future, unrelated blip again gets its one-shot recovery before we ever
  // give up on it.
  try {
    sessionStorage.removeItem(STORAGE_CORRUPTION_RETRY_KEY);
    sessionStorage.removeItem(DEVICE_MISMATCH_RECOVERED_KEY);
  } catch {
    /* sessionStorage unavailable — nothing to reset */
  }

  // Mount the developer console surface on `window.__ping.*` so dev
  // tooling and manual QA can drive every endpoint + SDK flow with
  // one-liners. Idempotent; safe to call on every bootstrap.
  mountConsoleApi();

  // Note: previously this site eagerly called
  // `bootstrapConversationsFromServer` to repopulate Dexie from
  // `GET /v1/conversations`. It was REMOVED because the call is
  // net-negative in every observed flow:
  //
  //   * Cold start (fresh signup / cache wiped): zero conversations
  //     on the BE → wasted roundtrip, and the brief device-activation
  //     race window 401s the first attempt anyway.
  //   * Warm reauth (returning user): the SDK's `syncConversations`
  //     already runs immediately after bootstrap and pulls envelopes
  //     directly from `/v1/messages?conv=`; the conv list is
  //     reconstructed from those envelopes by the chat-list adapter.
  //   * Mid-session refresh: the WS `convs.refresh` push gives us
  //     real-time updates without polling REST.
  //
  // Hosts that need to force a server-side conv-list refresh can
  // still invoke `bootstrapConversationsFromServer` directly via
  // `window.__ping.refreshConversationsCache()` — kept available in
  // the dev console surface for QA but not part of the auto-bootstrap.
  // FRESH-LEAF RE-LOGIN GATE (Task: resume conversations after sign-out).
  // When this authenticated load minted a BRAND-NEW SDK identity, the account's
  // pre-existing Welcomes were addressed to the OLD (wiped) device's KeyPackages,
  // so this fresh leaf can neither send nor receive in any prior conversation
  // until peers re-admit it. Set a one-shot marker so the recovery drain
  // (`ensureRecoveryDeliveryKeyAndDrain`) advertises this device's delivery key on
  // this load — peers then re-admit it to every shared conversation and ship the
  // history it missed while signed out. Mirrors iOS `PendingRecoveryPeerHistory`.
  // Only fires when the identity did NOT survive (fresh mint), which is exactly
  // the safe condition (never bait a peer into re-adding a still-live leaf).
  // The key string MUST match `recoveryPeerHistory.ts` `FRESH_LEAF_KEY`
  // (`__recovery.freshLeafRelogin`); written inline here (same pattern as the
  // `device.established` marker above) to avoid importing a feature module into
  // the session layer.
  if (isFreshIdentity) {
    try {
      const { db } = await import('@core/persistence/db');
      await db
        .table('settings_kv')
        .put({ key: '__recovery.freshLeafRelogin', value: true, updatedAt: Date.now() });
    } catch {
      /* best-effort — the recovery drain also re-advertises on later loads */
    }
  }

  // DIAGNOSTIC: compare the SDK's internal `ownDeviceId` against the
  // device_id the auth layer registered with the BE. If the bytes
  // differ, every envelope this session emits will fail
  // `expected_sender_device` validation on the message-service and
  // return 400 SenderDeviceMismatch. Auth stores `deviceId` as a
  // base64url string; the SDK returns raw bytes. We canonicalize both
  // sides to hex before comparing so the result reflects the actual
  // bytes, not an encoding artifact.
  // Device-id alignment check. The SDK stamps every envelope's
  // `sender_device` field with SHA-256(its signing pubkey); the auth
  // layer's JWT carries the device id the BE assigned at /v1/auth/verify
  // (also SHA-256 of the pubkey we sent at that point). They MUST agree
  // — when they don't, every `POST /v1/messages` returns 400
  // sender_device_mismatch and every other auth-gated route 401s.
  //
  // Previously this block was informational (just logged a `match: bool`
  // and kept going). That left the user in a "logged in but broken"
  // state — exactly the symptom in the console log this fix is for.
  // Now: on mismatch, wipe local state and force a clean reload. The
  // next page load is a cold start that re-signs in cleanly. There is
  // NO local-only recovery path for this — the SDK has already stamped
  // its identity with a signing key that disagrees with the BE's
  // record of this device, and the BE never rotates device pubkeys
  // for an existing account on its own.
  try {
    const sdkClientLike = finalStatus.client as unknown as {
      deviceId?: () => Promise<Uint8Array>;
      userId?: () => Promise<Uint8Array>;
      listConversations?: () => Promise<Array<{ id: Uint8Array }>>;
    };
    // Register THIS device's MLS account user id with the sync layer. With
    // shared-identity linking every device of the account reports the same id,
    // so the syncEngine can flag an incoming `sender_user_id === this` as one of
    // our OWN devices and render it as self — bulletproof, no broadcast map.
    if (typeof sdkClientLike.userId === 'function') {
      try {
        registerCurrentUserMlsUserId(bytesToHex(await sdkClientLike.userId()));
      } catch (err) {
        log.warn({
          event: 'session.bootstrap.registerMlsUserId.failed',
          message: (err as Error).message,
        });
      }
    }
    // Diagnostic: dump the SDK's rehydrated conversation set so we can tell
    // post-bootstrap whether a conv is missing from the in-memory map. If a
    // chat exists in Dexie but not here, sends will surface as
    // `mlsConversation.cache_stale` — that means rehydrate either didn't
    // find `groups/<id>/meta` in PingStorageWeb or `MlsGroup::load` returned
    // None for it (snapshot blob missing the group state). The diff between
    // this list and the Dexie chat list is the source of truth.
    if (typeof sdkClientLike.listConversations === 'function') {
      try {
        const convs = await sdkClientLike.listConversations();
        const ids = convs.map((m) => bytesToHex(m.id).slice(0, 8));
        log.info({
          event: 'session.bootstrap.sdkConversations',
          count: ids.length,
          ids,
        });
      } catch (err) {
        log.warn({
          event: 'session.bootstrap.sdkConversations.failed',
          message: (err as Error).message,
        });
      }
    }
    if (typeof sdkClientLike.deviceId === 'function') {
      const sdkDeviceIdBytes = await sdkClientLike.deviceId();
      const sdkDeviceIdHex = bytesToHex(sdkDeviceIdBytes);
      const persisted = await retrieveIdentity();
      const authDeviceIdStr = persisted?.deviceId ?? input.identity.deviceId;
      const authDeviceIdHex = base64urlStringToHex(authDeviceIdStr);
      const match = sdkDeviceIdHex === authDeviceIdHex;
      log.info({
        event: 'session.bootstrap.deviceIdDiag',
        sdkDeviceIdHex: sdkDeviceIdHex.slice(0, 32),
        authDeviceIdHex: authDeviceIdHex.slice(0, 32),
        authDeviceIdStr: authDeviceIdStr.slice(0, 16),
        match,
      });

      if (!match) {
        // The SDK's persisted device id disagrees with the auth layer's. The
        // one cause that survives a plain reload is stale SDK MLS storage: the
        // SDK loaded a device id persisted by a PRIOR init/identity instead of
        // adopting the device signing key we pass at init (whose
        // `SHA-256(pubkey)` IS the auth device id). The fix is to wipe ONLY the
        // SDK MLS storage so the next init has no persisted device and adopts
        // our signing key → the ids match by construction. We PRESERVE the
        // device signing key, identity seed AND credentials, so the realign is
        // silent (no re-OTP) and the BE still recognises this device.
        let alreadyRealigned = false;
        try {
          alreadyRealigned =
            sessionStorage.getItem(DEVICE_MISMATCH_RECOVERED_KEY) === '1';
        } catch {
          /* sessionStorage unavailable */
        }

        if (alreadyRealigned) {
          // We already wiped SDK storage + reloaded once and the device id STILL
          // disagrees → it is not stale MLS storage but the device signing key
          // itself that is wrong, which cannot be realigned client-side. Stop
          // auto-reloading (that loops) and surface the welcome screen with
          // credentials intact so a manual re-verify can re-bind the device.
          try {
            sessionStorage.removeItem(DEVICE_MISMATCH_RECOVERED_KEY);
          } catch {
            /* best-effort */
          }
          log.error(
            {
              event: 'session.bootstrap.deviceIdMismatch_unrecoverable',
              sdkDeviceIdHex: sdkDeviceIdHex.slice(0, 32),
              authDeviceIdHex: authDeviceIdHex.slice(0, 32),
            },
            'device id still mismatched after an SDK-storage realign — cannot fix client-side',
          );
          throw new BootstrapFailure(
            'invalidCredential',
            'device key mismatch persists after realign — please sign in again',
          );
        }

        // First mismatch: wipe the stale SDK MLS storage and reload ONCE so the
        // next init adopts the auth signing key. This actually fixes the
        // divergence (the old non-destructive multi-retry just re-loaded the
        // same stale device and "reload-looped"). Guarded one-shot so a
        // genuinely unfixable key mismatch can't loop.
        log.warn(
          {
            event: 'session.bootstrap.deviceIdMismatch_realigning',
            sdkDeviceIdHex: sdkDeviceIdHex.slice(0, 32),
            authDeviceIdHex: authDeviceIdHex.slice(0, 32),
          },
          'wiping stale SDK MLS storage so the next init adopts the auth signing key',
        );
        try {
          sessionStorage.setItem(DEVICE_MISMATCH_RECOVERED_KEY, '1');
        } catch {
          /* best-effort */
        }
        await wipeSdkStorageForRealign(storage);
        if (
          typeof window !== 'undefined' &&
          safeReload('bootstrap.deviceMismatch.realign')
        ) {
          await new Promise(() => {});
        }
        throw new BootstrapFailure(
          'invalidCredential',
          'device key mismatch — SDK storage realigned, retrying',
        );
      }

      // Healthy: clear the one-shot realign guard so a future, unrelated
      // mismatch in this tab can recover again.
      try {
        sessionStorage.removeItem(DEVICE_MISMATCH_RECOVERED_KEY);
      } catch {
        /* best-effort */
      }
    }
  } catch (diagErr) {
    // Don't rethrow non-mismatch diag failures — they're cosmetic.
    if (diagErr instanceof BootstrapFailure) throw diagErr;
    log.warn({
      event: 'session.bootstrap.deviceIdDiag.failed',
      message: (diagErr as Error).message,
    });
  }

  // Persist the caller-supplied identity (accountId + deviceId +
  // displayName) to Dexie, MERGING with whatever EnterCodeScreen
  // wrote at SMS-verify time. The displayName for a sign-up user
  // is captured *after* SMS verify (the password/displayName
  // /privacy steps), so the initial EnterCodeScreen
  // storeIdentity call wrote `displayName: ""`. The
  // coordinator's `resolveAuthenticatedUser` then collects the
  // displayName and may have called PUT /v1/profiles already.
  // Here we merge the freshest values from both sources so:
  //   - `NewChatModal` reads it for the profile-intro payload, and
  //   - peers see the correct sender name on incoming chats.
  // Order of precedence (highest first): input.identity field if
  // non-empty, then the previously persisted value.
  try {
    const existing = await retrieveIdentity();
    const merged = {
      accountId:
        input.identity.accountId.length > 0
          ? input.identity.accountId
          : (existing?.accountId ?? ''),
      deviceId:
        input.identity.deviceId.length > 0 ? input.identity.deviceId : (existing?.deviceId ?? ''),
      displayName:
        input.identity.displayName.length > 0
          ? input.identity.displayName
          : (existing?.displayName ?? ''),
    };
    if (merged.accountId.length > 0 && merged.deviceId.length > 0) {
      await storeIdentity(merged);
      // Register the current accountId with the @ping/ui sync layer so
      // `messagesAdapter.web` can compute `Message.isSelf` correctly —
      // without it, every bubble renders left-aligned because the
      // adapter has no way to distinguish our own sends (persisted
      // with `senderAccountId = identity.accountId`) from incoming
      // ones (persisted with `senderAccountId = hex(sender_device)`).
      registerCurrentUserAccountId(merged.accountId);
      registerCurrentUserDisplayName(merged.displayName ?? null);
      // Learn this account's device ids so a SIBLING device's messages render as
      // self (right-aligned) on this device. Fire-and-forget — message bubbles
      // re-align the moment it resolves (registerSelfDeviceIdsHex notifies the
      // message projection).
      void listSelfDeviceIdsHex(merged.accountId).then(registerSelfDeviceIdsHex);
      log.info({
        event: 'session.identity.persisted',
        hasDisplayName: merged.displayName.length > 0,
        source: input.identity.displayName.length > 0 ? 'flow' : 'persisted',
      });
    }
  } catch (err) {
    log.warn({ event: 'session.identity.persist_failed' }, (err as Error).message);
  }

  // Top up the device's KeyPackage pool so peers can invite us into
  // their groups (their `conv.addMembers([keyPackage])` consumes one
  // from our pool). Without this, the first peer to chat with us
  // hits 409 `key_package_pool_empty`. Non-blocking — the user can
  // already start sending; the replenish just makes us reachable.
  const authedClient = finalStatus.client as unknown as {
    freshKeyPackage(): Promise<Uint8Array>;
  };
  void registerWebPush();
  startEphemeralSession();

  if (typeof authedClient.freshKeyPackage === 'function') {
    // Only force a full re-upload when the on-server KeyPackages are unusable
    // regardless of count: a fresh identity, or a bootstrap right after the SDK
    // MLS store was wiped (realign / storage corruption) — where the private
    // init keys behind the published KeyPackages are gone. Otherwise the
    // replenish skips when the server pool is already healthy (no reload creep).
    const forceFullReplenish = isFreshIdentity || consumeForceReplenishMarker();
    void replenishKeyPackages(authedClient, { forceFull: forceFullReplenish });
    // Low-watermark top-up: keep this device's single-use KeyPackage pool
    // healthy as peers consume it over the session's lifetime. `latestKpClient`
    // always points at the current session's client, and the resume listeners
    // are registered ONCE (guarded) so re-bootstraps don't stack them.
    latestKpClient = authedClient;
    if (!topUpListenersRegistered && typeof window !== 'undefined') {
      topUpListenersRegistered = true;
      const onResume = (): void => {
        if (document.visibilityState !== 'visible') return;
        if (latestKpClient) void maybeTopUpKeyPackages(latestKpClient);
      };
      document.addEventListener('visibilitychange', onResume);
      window.addEventListener('online', onResume);
    }
    // Devtools handle: lets the user re-run replenish from the
    // console without reloading the tab. Useful when diagnosing
    // pool-empty 409s on a peer that's already logged in.
    //
    //   await window.__ping.replenishKeyPackages()
    //
    // Returns the BE's `{ accepted, pool_size_after }` on success.
    (window as unknown as { __ping?: Record<string, unknown> }).__ping = {
      ...(window as unknown as { __ping?: Record<string, unknown> }).__ping,
      replenishKeyPackages: () => replenishKeyPackages(authedClient),
    };
  } else {
    log.warn({
      event: 'session.bootstrap.no_freshKeyPackage',
    });
  }
}

interface LoadOrCreateIdentityResult {
  readonly handle: IdentityHandle;
  /**
   * `true` when no prior identity was stored locally — i.e. fresh
   * signup or post-wipe. The bootstrap path uses this to skip the
   * `GET /v1/conversations` merge (a brand-new device has zero
   * conversations on the BE, so the call wastes a roundtrip + briefly
   * 401s during the device-activation race window).
   */
  readonly isFreshIdentity: boolean;
}

async function loadOrCreateIdentity(
  storage: PingStorageWeb,
): Promise<LoadOrCreateIdentityResult> {
  let existingSeed: Uint8Array | null;
  try {
    existingSeed = await identityManager.unwrapIdentity();
  } catch (error) {
    // A stored identity we cannot read RIGHT NOW must NEVER trigger a
    // credential/identity wipe here. Wiping the refresh token + regenerating
    // identity is what caused "signed out randomly + lost conversations + can't
    // sign back in": the fresh identity presents a new user_identity_pubkey and
    // the BE 403s `identity_mismatch` (this device is still server-side active),
    // locking the legitimate owner out of their own account. Both failure modes
    // are handled WITHOUT destroying anything, so the refresh token + device
    // signing key survive and the app recovers:
    //   - IdentityKeyUnavailableError (transient): the wrap key was evicted
    //     while the ciphertext survives (Safari/ITP eviction, quota pressure, or
    //     a concurrent forgetIdentity mid-delete). Re-throw → the silent-reauth
    //     loop retries on the cached shell; a later read usually succeeds.
    //   - IdentityCorruptionError (genuine): the ciphertext truly won't decrypt.
    //     We STILL don't wipe — the only correct recovery for a pinned, active
    //     device is the mnemonic recovery flow or re-linking from another
    //     device, both reached from the welcome screen. Re-throw → the bootstrap
    //     failure path (`goUnauthenticated` without fullWipe) lands the user on
    //     welcome with credentials preserved.
    if (error instanceof IdentityKeyUnavailableError || error instanceof IdentityCorruptionError) {
      log.warn(
        { event: 'session.identity.unreadable', kind: error.name },
        'stored identity unreadable this load; preserving credentials (no wipe)',
      );
    }
    throw error;
  }

  if (existingSeed !== null) {
    return {
      handle: {
        _brand: 'IdentityHandle' as const,
        identityExport: existingSeed,
      },
      isFreshIdentity: false,
    };
  }

  // SUSPECT-ESTABLISHED GUARD. Reaching here means `unwrapIdentity()` returned
  // `null` (no stored identity seed). For a genuine fresh signup / post-sign-out
  // clean slate that is correct. But if this device has previously completed an
  // authenticated bootstrap (the `device.established` marker survives everything
  // EXCEPT a full sign-out, which also wipes it), a null identity is NOT a fresh
  // start — it is an established device whose identity seed went missing without a
  // sign-out (storage eviction / a partial-wipe bug). Minting a fresh identity
  // there would (a) get 403 `identity_mismatch` from the BE (this device is still
  // server-side active + identity-pinned → lockout) AND (b) wipe every
  // conversation below. So DON'T: preserve the credentials + all chat data and
  // route the user to recovery/re-link, exactly like the corruption path. This is
  // the last-line guard against "all my conversations vanished on a reload".
  if (await isDeviceEstablished()) {
    log.error(
      { event: 'session.identity.null_on_established_device' },
      'stored identity missing on a device that was already established — preserving credentials + conversations (no fresh-identity wipe)',
    );
    throw new BootstrapFailure(
      'invalidCredential',
      'your identity data is missing — recover from your recovery phrase or another device',
    );
  }

  // Generating a fresh identity means there is no MLS state on this
  // device that belongs to "us" — any leftover SDK storage was either
  // owned by a now-gone identity or written by a half-finished init
  // that crashed mid-flight. Either way it cannot be safely reused;
  // wipe it so MessagingClient.init starts against a clean slate.
  log.info(
    { event: 'session.storage.resetting_for_fresh_identity' },
    'wiping SDK storage before fresh identity init',
  );
  await storage.forgetSession();

  // Same logic applies to the IndexedDB chat cache + the per-
  // conversation WS-resume cursor store + the Dexie chat tables. All
  // three are keyed by conversation id; a fresh identity isn't a
  // member of any of those convs, so leaving the rows in place would
  // (a) show ghost chats on the chat list, (b) make the next WS resume
  // frame replay cursors for convs the BE will 404 on, and (c) cause
  // the SDK's `syncConversations` to hit 404 on `/v1/messages?conv=…`
  // for those orphaned ids — exactly the failure mode reported in
  // production console logs after sign-up.
  //
  // Best-effort: failures here don't gate bootstrap.
  try {
    const { clearAllData } = await import('@core/storage/idb');
    await clearAllData();
  } catch (err) {
    log.warn({
      event: 'session.storage.idb_clear_failed',
      message: (err as Error).message,
    });
  }
  try {
    const { db } = await import('@core/persistence/db');
    await Promise.all([
      db.table('messages').clear(),
      db.table('conversations').clear(),
      db.table('drafts').clear(),
      db.table('attachments_meta').clear(),
    ]);
    log.info({ event: 'session.storage.dexie_cleared_for_fresh_identity' });
  } catch (err) {
    log.warn({
      event: 'session.storage.dexie_clear_failed',
      message: (err as Error).message,
    });
  }

  // NOTE: there is deliberately NO "identity corruption → wipe tokens" branch
  // here anymore. An unreadable stored identity is handled upstream in the
  // unwrap catch by RE-THROWING with credentials preserved (never wiping the
  // refresh token or regenerating identity), because a regenerated identity on
  // a still-active, identity-pinned device is rejected by the BE with 403
  // `identity_mismatch` — the exact "can't sign back in / lost conversations"
  // lockout. We only reach THIS point when `unwrapIdentity` returned `null`
  // (genuinely no prior identity on this device = a fresh signup / post-logout
  // clean slate), for which minting a new identity below is correct.

  // The SDK owns the identity export format (CBOR-encoded
  // `{ v: 1, seed }`). The web `identityManager.generateIdentity()`
  // historically returned 32 raw random bytes, which `Identity::import`
  // rejects as malformed CBOR (`Syntax(10)`). Call the SDK's static
  // factory so the bytes are in the format the SDK expects to import.
  const identityExport = await MessagingClient.generateIdentity();
  await identityManager.wrapIdentity(identityExport);
  return {
    handle: {
      _brand: 'IdentityHandle' as const,
      identityExport,
    },
    isFreshIdentity: true,
  };
}

/**
 * Map an SDK / network error into the cross-platform
 * `AuthFailureReason` enum without leaking the underlying message.
 *
 * The Ping OpenMLS SDK exposes opaque variant names only — we may not
 * stringify the cause. `AuthFailureReason` is the contract WEB-AUTH
 * surfaces in the failure step; pick the closest semantic bucket.
 */
function mapBootstrapError(error: unknown): AuthFailureReason {
  if (error instanceof BootstrapFailure) {
    return error.reason;
  }
  if (typeof error === 'object' && error !== null && 'name' in error) {
    const name = (error as { name?: unknown }).name;
    if (name === 'NetworkError') {
      return 'network';
    }
  }
  return 'serverError';
}

function errorKind(error: unknown): string {
  if (error instanceof Error) {
    return error.name;
  }
  return 'unknown';
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}

function base64urlStringToHex(s: string): string {
  try {
    const padding = '='.repeat((4 - (s.length % 4)) % 4);
    const standard = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(standard);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return bytesToHex(out);
  } catch {
    return '<undecodable>';
  }
}
